<section class="av-card p-4 d-flex flex-column gap-3" data-testid="plan-detail-header">
  <div class="d-flex flex-wrap align-items-start justify-content-between gap-3">
    <div class="d-flex flex-column gap-1">
      <a class="av-meta d-inline-flex align-items-center gap-1 back-link" routerLink="/plans">
        <mat-icon aria-hidden="true">arrow_back</mat-icon>
        {{ 'backoffice.plans.detail.back' | translate }}
      </a>

      <h2 class="av-section-title m-0" data-testid="plan-detail-name">
        {{ facade.plan()?.displayName || unresolved }}
      </h2>

      <p class="av-meta m-0">{{ facade.plan()?.code || unresolved }}</p>
    </div>

    <div class="d-flex align-items-center gap-2">
      @if (facade.plan(); as plan) {
        <av-status-chip
          [label]="
            (plan.is_active ? 'backoffice.common.active' : 'backoffice.common.inactive') | translate
          "
          [tone]="plan.is_active ? 'positive' : 'neutral'"
        ></av-status-chip>
      }

      <button type="button" class="av-btn" (click)="facade.reload()">
        <mat-icon aria-hidden="true">refresh</mat-icon>
        {{ 'backoffice.common.actions.refresh' | translate }}
      </button>
    </div>
  </div>
</section>

<section class="av-card mt-3 d-flex flex-column" data-testid="plan-detail-body">
  @switch (facade.state()) {
    @case ('error') {
      <div class="p-4">
        <av-empty-state
          icon="error_outline"
          [title]="'backoffice.plans.detail.error.title' | translate"
          [message]="'backoffice.plans.detail.error.message' | translate"
        ></av-empty-state>
      </div>
    }
    @case ('ready') {
      <nav class="tab-bar d-flex flex-wrap" role="tablist" data-testid="plan-tabs">
        @for (tab of tabs; track tab.id) {
          <button
            type="button"
            role="tab"
            class="tab-button d-inline-flex align-items-center gap-2"
            [class.is-active]="activeTab() === tab.id"
            [attr.aria-selected]="activeTab() === tab.id"
            [attr.data-tab]="tab.id"
            (click)="selectTab(tab.id)"
          >
            <mat-icon aria-hidden="true">{{ tab.icon }}</mat-icon>
            {{ tab.label | translate }}
          </button>
        }
      </nav>

      <div class="p-4" data-testid="plan-tab-panel">
        @switch (activeTab()) {
          @case ('prices') {
            <av-plan-prices></av-plan-prices>
          }
          @case ('features') {
            <av-plan-features></av-plan-features>
          }
          @case ('subscribers') {
            <av-plan-subscribers></av-plan-subscribers>
          }
          @default {
            @if (facade.plan(); as plan) {
              <div class="d-flex flex-column gap-4">
                <section class="d-flex flex-column gap-2">
                  <h3 class="av-section-title m-0">
                    {{ 'backoffice.plans.detail.identity_title' | translate }}
                  </h3>

                  <dl class="detail-grid m-0">
                    <dt class="av-meta">
                      {{ 'backoffice.plans.detail.facts.code' | translate }}
                    </dt>
                    <dd data-testid="plan-code">{{ plan.code || unresolved }}</dd>

                    <dt class="av-meta">
                      {{ 'backoffice.plans.detail.facts.market' | translate }}
                    </dt>
                    <dd>{{ plan.marketLabel }}</dd>

                    <dt class="av-meta">
                      {{ 'backoffice.plans.detail.facts.currency' | translate }}
                    </dt>
                    <dd>{{ plan.currency || unresolved }}</dd>

                    <dt class="av-meta">
                      {{ 'backoffice.plans.detail.facts.version' | translate }}
                    </dt>
                    <dd>{{ plan.version }}</dd>

                    <dt class="av-meta">
                      {{ 'backoffice.plans.detail.facts.subscribers' | translate }}
                    </dt>
                    <dd>{{ plan.subscriber_count }}</dd>

                    <dt class="av-meta">
                      {{ 'backoffice.plans.detail.facts.created' | translate }}
                    </dt>
                    <dd>
                      {{ plan.created_at ? (plan.created_at | date: dateTimeFormat) : unresolved }}
                    </dd>

                    <dt class="av-meta">
                      {{ 'backoffice.plans.detail.facts.updated' | translate }}
                    </dt>
                    <dd>
                      {{ plan.updated_at ? (plan.updated_at | date: dateTimeFormat) : unresolved }}
                    </dd>
                  </dl>
                </section>

                <section class="d-flex flex-column gap-2">
                  <h3 class="av-section-title m-0">
                    {{ 'backoffice.plans.detail.edit_title' | translate }}
                  </h3>
                  <p class="av-meta m-0">{{ 'backoffice.plans.detail.edit_hint' | translate }}</p>

                  @if (!facade.canWrite()) {
                    <p class="av-meta m-0" data-testid="plan-edit-restricted">
                      {{ 'backoffice.plans.list.restricted_note' | translate }}
                    </p>
                  } @else {
                    <form
                      [formGroup]="formGroup"
                      class="d-flex flex-wrap align-items-end gap-3"
                      (ngSubmit)="submit()"
                    >
                      <label class="d-flex flex-column gap-1 flex-grow-1" for="plan-edit-name">
                        <span class="av-meta">
                          {{ 'backoffice.plans.detail.form.name' | translate }}
                        </span>
                        <input
                          id="plan-edit-name"
                          type="text"
                          class="av-field w-100"
                          formControlName="name"
                        />
                      </label>

                      <label class="d-flex align-items-center gap-2">
                        <input type="checkbox" formControlName="is_active" />
                        <span class="av-section-hint">
                          {{ 'backoffice.plans.detail.form.active' | translate }}
                        </span>
                      </label>

                      <button
                        type="submit"
                        class="av-btn av-btn-primary"
                        [disabled]="!formGroup.valid || facade.isMutating()"
                      >
                        <mat-icon aria-hidden="true">save</mat-icon>
                        {{ 'backoffice.plans.detail.form.save' | translate }}
                      </button>
                    </form>
                  }
                </section>
              </div>
            }
          }
        }
      </div>
    }
    @default {
      <div class="p-4">
        <av-empty-state
          icon="hourglass_empty"
          [title]="'backoffice.plans.detail.loading.title' | translate"
          [message]="'backoffice.plans.detail.loading.message' | translate"
        ></av-empty-state>
      </div>
    }
  }
</section>
